import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState([]);

  const fetchWishlist = useCallback(async () => {
    if (!user) { setWishlist([]); return; }
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(res.data.items || []);
    } catch { setWishlist([]); }
  }, [user]);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  const addToWishlist = async (product) => {
    if (!user) return false;
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/wishlist/${product.id}`, {
        product_name: product.name,
        product_price: product.price,
        product_image: product.images?.[0]?.src || ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      setWishlist(prev => [...prev, {
        product_id: product.id,
        product_name: product.name,
        product_price: product.price,
        product_image: product.images?.[0]?.src || '',
        added_at: new Date().toISOString()
      }]);
      return true;
    } catch { return false; }
  };

  const removeFromWishlist = async (productId) => {
    if (!user) return false;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/wishlist/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(prev => prev.filter(i => i.product_id !== productId));
      return true;
    } catch { return false; }
  };

  const toggleWishlist = async (product) => {
    if (isInWishlist(product.id)) {
      return removeFromWishlist(product.id);
    }
    return addToWishlist(product);
  };

  const isInWishlist = (productId) => wishlist.some(i => i.product_id === productId);

  return (
    <WishlistContext.Provider value={{ wishlist, addToWishlist, removeFromWishlist, toggleWishlist, isInWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be inside WishlistProvider');
  return ctx;
}
